/**
 * Cloud File Select Content
 *
 * The inner content of the cloud file selection UI, extracted from CloudFileSelectModal.
 * Can be embedded directly in a page (e.g., upload page) or wrapped in a Dialog.
 *
 * Uses local state for selection management, making each instance
 * independent and properly isolated.
 *
 * The component can be used in two modes:
 * 1. With onUpload callback - component handles its own upload button
 * 2. With onSelectionChange callback - parent handles the upload action
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Slider,
  Chip,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { CloudFileTree } from "./CloudFileTree";
import { CloudFileBreadcrumbs } from "./CloudFileBreadcrumbs";
import { FileIcon, FILE_ICON_COLORS } from "./FileIcon";
import { usePermission } from "src/helpers/Auth";
import {
  useCloudProviders,
  useCloudConnections,
  formatFileSize,
} from "../services/CloudFileServices";
import { toActiveExtensionFilters } from "../utils/extensionFilters";
import {
  CLOUD_PERMISSIONS,
  type CloudProviderType,
  type CloudFileMetadata,
} from "src/types/cloudFiles";

// =============================================================================
// Types
// =============================================================================

/**
 * Options for folder upload behavior.
 * These control how folders are traversed and filtered during cloud imports.
 */
export interface CloudUploadOptions {
  /** Per-folder import options, keyed by folder path */
  folderOptions: Map<string, FolderImportOptions>;
}

/**
 * Per-folder import options stored by folder path.
 */
export interface FolderImportOptions {
  maxFolderDepth: number;
  filterByExtension: boolean;
  selectedExtensions: string[];
}

/** Default options for a newly selected folder */
export const DEFAULT_FOLDER_OPTIONS: FolderImportOptions = {
  maxFolderDepth: 0,
  filterByExtension: false,
  selectedExtensions: [],
};

/**
 * Selection state exposed via ref for parent components
 */
export interface CloudFileSelectState {
  selectedProvider: CloudProviderType | "";
  selectedConnectionId: number | "";
  selectedFiles: CloudFileMetadata[];
  selectedFolders: CloudFileMetadata[];
  folderOptions: Map<string, FolderImportOptions>;
  totalSelected: number;
  canUpload: boolean;
  canUseCloud: boolean;
}

/**
 * Ref handle for CloudFileSelectContent
 */
export interface CloudFileSelectContentRef {
  /** Get current selection state */
  getState: () => CloudFileSelectState;
  /** Clear all selections */
  clearSelection: () => void;
  /** Trigger upload with current selection */
  triggerUpload: () => void;
}

export interface CloudFileSelectContentProps {
  /** Target dataset ID (for context) */
  datasetId?: number;
  /** Callback when files are selected for upload (if not provided, use ref to get state) */
  onUpload?: (
    providerType: CloudProviderType,
    connectionId: number,
    files: CloudFileMetadata[],
    folders: CloudFileMetadata[],
    options: CloudUploadOptions,
  ) => void;
  /** Callback when selection changes (for external state management) */
  onSelectionChange?: (state: CloudFileSelectState) => void;
  /** Whether to show the built-in upload button */
  showUploadButton?: boolean;
  /** Pre-selected provider type */
  initialProvider?: CloudProviderType;
  /** Pre-selected connection ID */
  initialConnectionId?: number;
  /** Whether an upload is currently in progress */
  isUploading?: boolean;
}

// =============================================================================
// Selected Files Panel
// =============================================================================

interface SelectedFilesPanelProps {
  selectedFiles: CloudFileMetadata[];
  selectedFolders: CloudFileMetadata[];
  folderOptions: Map<string, FolderImportOptions>;
  onRemoveFile: (path: string) => void;
  onRemoveFolder: (path: string) => void;
  onUpdateFolderOptions: (path: string, options: FolderImportOptions) => void;
  onClearAll: () => void;
}

/**
 * Folder item with expandable import options
 */
const FolderListItem: React.FC<{
  folder: CloudFileMetadata;
  options: FolderImportOptions;
  onRemove: () => void;
  onUpdateOptions: (options: FolderImportOptions) => void;
}> = ({ folder, options, onRemove, onUpdateOptions }) => {
  const [expanded, setExpanded] = useState(false);
  const [extensionInput, setExtensionInput] = useState("");

  const handleAddExtension = () => {
    const ext = extensionInput.trim().toLowerCase();
    if (ext && !options.selectedExtensions.includes(ext)) {
      const normalizedExt = ext.startsWith(".") ? ext : `.${ext}`;
      if (!options.selectedExtensions.includes(normalizedExt)) {
        onUpdateOptions({
          ...options,
          selectedExtensions: [...options.selectedExtensions, normalizedExt],
        });
      }
    }
    setExtensionInput("");
  };

  const handleRemoveExtension = (ext: string) => {
    onUpdateOptions({
      ...options,
      selectedExtensions: options.selectedExtensions.filter((e) => e !== ext),
    });
  };

  // Build hint text showing current options when collapsed
  const getOptionsHint = () => {
    const parts: string[] = [];
    parts.push(
      `Max Depth: ${
        options.maxFolderDepth === 0 ? "∞" : options.maxFolderDepth
      }`,
    );
    if (options.filterByExtension && options.selectedExtensions.length > 0) {
      parts.push(`Extensions: ${options.selectedExtensions.join(", ")}`);
    }
    return parts.join(" • ");
  };

  return (
    <Box
      sx={{ mb: 0.5 }}
      data-testid={`cloud-folder-item-${folder.path.replace(/\//g, "--")}`}
    >
      <ListItem
        sx={{
          bgcolor: "rgba(0,0,0,0.04)",
          borderRadius: expanded ? "4px 4px 0 0" : 1,
          cursor: "pointer",
          "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
          alignItems: "flex-start",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: 40,
            flexShrink: 0,
            ml: -0.5,
          }}
        >
          {expanded ? (
            <ExpandMoreIcon
              sx={{ fontSize: 18, color: FILE_ICON_COLORS.folder }}
            />
          ) : (
            <ChevronRightIcon
              sx={{ fontSize: 18, color: FILE_ICON_COLORS.folder }}
            />
          )}
          <FileIcon
            filename={folder.name}
            isDirectory={true}
            fontSize={20}
            sx={{ mr: 0.5 }}
          />
        </Box>
        <ListItemText
          primary={folder.name}
          secondary={
            expanded ? (
              <Typography
                component="span"
                variant="caption"
                sx={{ color: "text.secondary" }}
              >
                {folder.path}
              </Typography>
            ) : (
              <Box component="span" sx={{ display: "block" }}>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block" }}
                  noWrap
                >
                  {folder.path}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    position: "relative",
                    overflow: "hidden",
                    maxWidth: "100%",
                  }}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      color: "text.disabled",
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      maskImage:
                        "linear-gradient(to right, black 85%, transparent 100%)",
                      WebkitMaskImage:
                        "linear-gradient(to right, black 85%, transparent 100%)",
                    }}
                  >
                    {getOptionsHint()}
                  </Typography>
                </Box>
              </Box>
            )
          }
          primaryTypographyProps={{
            variant: "body2",
            noWrap: true,
          }}
          secondaryTypographyProps={{
            component: "div",
          }}
        />
        <ListItemSecondaryAction>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            data-testid={`cloud-folder-remove-${folder.path.replace(/\//g, "--")}`}
            sx={{
              color: "text.disabled",
              "& .cancel-icon": { display: "none" },
              "& .checked-icon": { display: "block" },
              "&:hover .cancel-icon": { display: "block" },
              "&:hover .checked-icon": { display: "none" },
            }}
          >
            <CheckCircleOutlinedIcon
              fontSize="small"
              className="checked-icon"
            />
            <CancelOutlinedIcon fontSize="small" className="cancel-icon" />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>

      {/* Expandable options panel */}
      {expanded && (
        <Box
          data-testid={`cloud-folder-options-${folder.path.replace(/\//g, "--")}`}
          sx={{
            bgcolor: "rgba(0,0,0,0.06)",
            borderRadius: "0 0 4px 4px",
            p: 1.5,
            mb: 0.5,
          }}
        >
          {/* Max Folder Depth */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Max Depth:{" "}
              {options.maxFolderDepth === 0 ? "∞" : options.maxFolderDepth}
            </Typography>
            <Slider
              value={
                options.maxFolderDepth === 0
                  ? 100
                  : Math.log2(options.maxFolderDepth) * 10
              }
              onChange={(_, sliderValue) => {
                const val = sliderValue as number;
                if (val >= 95) {
                  onUpdateOptions({ ...options, maxFolderDepth: 0 });
                } else {
                  const depth = Math.round(Math.pow(2, val / 10));
                  onUpdateOptions({
                    ...options,
                    maxFolderDepth: Math.max(1, depth),
                  });
                }
              }}
              min={0}
              max={100}
              step={1}
              marks={[
                { value: 0, label: "1" },
                { value: 10, label: "2" },
                { value: 20, label: "4" },
                { value: 30, label: "8" },
                { value: 50, label: "32" },
                { value: 100, label: "∞" },
              ]}
              size="small"
              valueLabelDisplay="off"
              data-testid="cloud-folder-depth-slider"
              sx={{
                "& .MuiSlider-markLabel": {
                  fontSize: "0.65rem",
                  color: "text.disabled",
                },
              }}
            />
          </Box>

          {/* Extension Filter */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={options.filterByExtension}
                  onChange={(e) =>
                    onUpdateOptions({
                      ...options,
                      filterByExtension: e.target.checked,
                    })
                  }
                  size="small"
                  data-testid="cloud-folder-ext-filter-switch"
                />
              }
              label={
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Filter by extension
                </Typography>
              }
            />

            {options.filterByExtension && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder=".pdf, .docx"
                    value={extensionInput}
                    onChange={(e) => setExtensionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        handleAddExtension();
                      }
                    }}
                    data-testid="cloud-folder-ext-input"
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        bgcolor: "white",
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddExtension}
                    disabled={!extensionInput.trim()}
                    data-testid="cloud-folder-ext-add-btn"
                    sx={{
                      fontSize: "0.7rem",
                      minWidth: "auto",
                      px: 1,
                    }}
                  >
                    Add
                  </Button>
                </Box>

                {options.selectedExtensions.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {options.selectedExtensions.map((ext) => (
                      <Chip
                        key={ext}
                        label={ext}
                        size="small"
                        onDelete={() => handleRemoveExtension(ext)}
                        sx={{
                          height: 20,
                          fontSize: "0.7rem",
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const SelectedFilesPanel: React.FC<SelectedFilesPanelProps> = ({
  selectedFiles,
  selectedFolders,
  folderOptions,
  onRemoveFile,
  onRemoveFolder,
  onUpdateFolderOptions,
  onClearAll,
}) => {
  const totalItems = selectedFiles.length + selectedFolders.length;
  const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#f5f5f5",
        color: "text.primary",
        borderRadius: 0,
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
        <Typography variant="h6">Files Selected:</Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary" }}
          data-testid="cloud-selected-count"
        >
          {totalItems} item{totalItems !== 1 ? "s" : ""} •{" "}
          {formatFileSize(totalSize)}
        </Typography>
      </Box>

      {/* Selected items list */}
      <Box sx={{ flex: 1, overflowX: "hidden", overflowY: "auto", p: 1 }}>
        {totalItems === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              opacity: 0.6,
            }}
          >
            <Typography variant="body2">
              Click files or folders to select them
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {/* Folders first - with expandable options */}
            {selectedFolders.map((folder) => (
              <FolderListItem
                key={folder.path}
                folder={folder}
                options={
                  folderOptions.get(folder.path) || DEFAULT_FOLDER_OPTIONS
                }
                onRemove={() => onRemoveFolder(folder.path)}
                onUpdateOptions={(opts) =>
                  onUpdateFolderOptions(folder.path, opts)
                }
              />
            ))}

            {/* Then files */}
            {selectedFiles.map((file) => (
              <ListItem
                key={file.path}
                data-testid={`cloud-file-item-${file.path.replace(/\//g, "--")}`}
                sx={{
                  bgcolor: "rgba(0,0,0,0.04)",
                  borderRadius: 1,
                  mb: 0.5,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                }}
              >
                {/* Spacer to align with folder caret */}
                <Box sx={{ width: 18, ml: -0.5 }} />
                <FileIcon
                  filename={file.name}
                  isDirectory={false}
                  fontSize={20}
                  sx={{ mr: 0.5 }}
                />
                <ListItemText
                  primary={file.name}
                  secondary={`${file.path} • ${formatFileSize(file.size)}`}
                  primaryTypographyProps={{
                    variant: "body2",
                    noWrap: true,
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    noWrap: true,
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveFile(file.path)}
                    data-testid={`cloud-file-remove-${file.path.replace(/\//g, "--")}`}
                    sx={{
                      color: "text.disabled",
                      "& .cancel-icon": { display: "none" },
                      "& .checked-icon": { display: "block" },
                      "&:hover .cancel-icon": { display: "block" },
                      "&:hover .checked-icon": { display: "none" },
                    }}
                  >
                    <CheckCircleOutlinedIcon
                      fontSize="small"
                      className="checked-icon"
                    />
                    <CancelOutlinedIcon
                      fontSize="small"
                      className="cancel-icon"
                    />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Footer with clear all button */}
      {totalItems > 0 && (
        <Box sx={{ p: 2, borderTop: "1px solid rgba(0,0,0,0.12)" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearAllIcon />}
            onClick={onClearAll}
            fullWidth
            data-testid="cloud-clear-selection"
          >
            Unselect All
          </Button>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const CloudFileSelectContent = forwardRef<
  CloudFileSelectContentRef,
  CloudFileSelectContentProps
>(
  (
    {
      datasetId: _datasetId,
      onUpload,
      onSelectionChange,
      showUploadButton = false,
      initialProvider,
      initialConnectionId,
      isUploading = false,
    },
    ref,
  ) => {
    // Local state for provider/connection selection
    const [selectedProvider, setSelectedProvider] = useState<
      CloudProviderType | ""
    >(initialProvider || "");
    const [selectedConnectionId, setSelectedConnectionId] = useState<
      number | ""
    >(initialConnectionId || "");

    // Local state for file/folder selection
    const [selectedFiles, setSelectedFiles] = useState<CloudFileMetadata[]>([]);
    const [selectedFolders, setSelectedFolders] = useState<CloudFileMetadata[]>(
      [],
    );

    // Per-folder import options (keyed by folder path)
    const [folderOptions, setFolderOptions] = useState<
      Map<string, FolderImportOptions>
    >(new Map());

    // Breadcrumb navigation state
    const [currentBreadcrumbPath, setCurrentBreadcrumbPath] = useState("/");

    // Permission checks for cloud operations
    const { canIt: canViewCloud, isLoading: isLoadingViewPermission } =
      usePermission(CLOUD_PERMISSIONS.VIEW);
    const { canIt: canUseCloud, isLoading: isLoadingUsePermission } =
      usePermission(CLOUD_PERMISSIONS.USE);
    const isLoadingPermissions =
      isLoadingViewPermission || isLoadingUsePermission;

    // Fetch providers and connections
    const {
      data: providers,
      isLoading: isLoadingProviders,
      isError: isProvidersError,
      error: providersError,
      refetch: refetchProviders,
    } = useCloudProviders();
    const {
      data: connections,
      isLoading: isLoadingConnections,
      isError: isConnectionsError,
      error: connectionsError,
      refetch: refetchConnections,
    } = useCloudConnections(selectedProvider || null);

    // Computed values
    const totalSelected = selectedFiles.length + selectedFolders.length;
    const canUpload =
      totalSelected > 0 && !!selectedProvider && !!selectedConnectionId;
    const folderExtensionFilters = useMemo(() => {
      const selectedPaths = new Set(selectedFolders.map((f) => f.path));
      return toActiveExtensionFilters(folderOptions, selectedPaths);
    }, [folderOptions, selectedFolders]);

    // Clear selection helper - keeps provider, connection, and current path
    const clearSelection = useCallback(() => {
      setSelectedFiles([]);
      setSelectedFolders([]);
      setFolderOptions(new Map());
      // NOTE: Intentionally NOT resetting currentBreadcrumbPath or provider/connection
      // to allow users to continue browsing and selecting more files after upload
    }, []);

    // Handle upload - can be called via ref or built-in button
    const handleUpload = useCallback(() => {
      if (!selectedProvider || !selectedConnectionId || !onUpload) return;

      // Build options object with per-folder settings
      const options: CloudUploadOptions = {
        folderOptions: folderOptions,
      };

      onUpload(
        selectedProvider,
        selectedConnectionId,
        selectedFiles,
        selectedFolders,
        options,
      );

      // Clear selection after upload starts
      clearSelection();
    }, [
      selectedProvider,
      selectedConnectionId,
      selectedFiles,
      selectedFolders,
      folderOptions,
      onUpload,
      clearSelection,
    ]);

    // Build current state for external consumers
    const getState = useCallback(
      (): CloudFileSelectState => ({
        selectedProvider,
        selectedConnectionId,
        selectedFiles,
        selectedFolders,
        folderOptions,
        totalSelected,
        canUpload,
        canUseCloud: canUseCloud ?? false,
      }),
      [
        selectedProvider,
        selectedConnectionId,
        selectedFiles,
        selectedFolders,
        folderOptions,
        totalSelected,
        canUpload,
        canUseCloud,
      ],
    );

    // Expose methods via ref for parent components
    useImperativeHandle(
      ref,
      () => ({
        getState,
        clearSelection,
        triggerUpload: handleUpload,
      }),
      [getState, clearSelection, handleUpload],
    );

    // Notify parent of selection changes
    useEffect(() => {
      if (onSelectionChange) {
        onSelectionChange(getState());
      }
    }, [
      selectedProvider,
      selectedConnectionId,
      selectedFiles,
      selectedFolders,
      folderOptions,
      canUseCloud,
      onSelectionChange,
      getState,
    ]);

    // Handle breadcrumb navigation
    const handleBreadcrumbNavigate = useCallback((path: string) => {
      setCurrentBreadcrumbPath(path);
    }, []);

    // Handle folder navigation (double-clicking a folder in the tree)
    const handleFolderNavigate = useCallback((folder: CloudFileMetadata) => {
      setCurrentBreadcrumbPath(folder.path);
    }, []);

    // Handle selection change from CloudFileTree
    const handleSelectionChangeInternal = useCallback(
      (files: CloudFileMetadata[], folders: CloudFileMetadata[]) => {
        setSelectedFiles(files);
        setSelectedFolders(folders);
      },
      [],
    );

    // Remove a file from selection
    const handleRemoveFile = useCallback((path: string) => {
      setSelectedFiles((prev) => prev.filter((f) => f.path !== path));
    }, []);

    // Remove a folder from selection
    const handleRemoveFolder = useCallback((path: string) => {
      setSelectedFolders((prev) => prev.filter((f) => f.path !== path));
      setFolderOptions((prev) => {
        const next = new Map(prev);
        next.delete(path);
        return next;
      });
    }, []);

    // Update import options for a specific folder
    const handleUpdateFolderOptions = useCallback(
      (path: string, options: FolderImportOptions) => {
        setFolderOptions((prev) => {
          const next = new Map(prev);
          next.set(path, options);
          return next;
        });
      },
      [],
    );

    // Reset state when initial values change
    useEffect(() => {
      if (initialProvider) {
        setSelectedProvider(initialProvider);
      }
      if (initialConnectionId) {
        setSelectedConnectionId(initialConnectionId);
      }
    }, [initialProvider, initialConnectionId]);

    // Handle provider change
    const handleProviderChange = (provider: CloudProviderType) => {
      setSelectedProvider(provider);
      setSelectedConnectionId("");
      clearSelection();
    };

    // Handle connection change
    const handleConnectionChange = (connectionId: number) => {
      setSelectedConnectionId(connectionId);
      clearSelection();
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 400,
          flex: 1,
        }}
      >
        {/* Permission check - loading state */}
        {isLoadingPermissions && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              p: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Permission check - no view permission */}
        {!isLoadingPermissions && !canViewCloud && (
          <Box sx={{ width: "100%", p: 3 }}>
            <Alert severity="error" data-testid="cloud-permission-error">
              You don't have permission to browse cloud files. Contact your
              administrator to request access.
            </Alert>
          </Box>
        )}

        {/* Permission check - can view but cannot use (upload) */}
        {!isLoadingPermissions && canViewCloud && !canUseCloud && (
          <Box sx={{ width: "100%", p: 3 }}>
            <Alert
              severity="warning"
              data-testid="cloud-view-only-warning"
              sx={{ mb: 2 }}
            >
              You can browse cloud files but cannot import them. Contact your
              administrator to request upload permissions.
            </Alert>
          </Box>
        )}

        {/* Main content - only show when permissions allow */}
        {!isLoadingPermissions && canViewCloud && (
          <Box
            sx={{
              display: "flex",
              flex: 1,
              overflow: "hidden",
            }}
          >
            {/* Left Panel - Provider selection and file tree */}
            <Box
              sx={{
                flex: 2,
                display: "flex",
                flexDirection: "column",
                borderRight: 1,
                borderColor: "divider",
              }}
            >
              {/* Provider/Connection selectors */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                {/* Error alerts for provider/connection fetch failures */}
                {isProvidersError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    data-testid="cloud-provider-error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => refetchProviders()}
                      >
                        Retry
                      </Button>
                    }
                  >
                    Failed to load cloud providers:{" "}
                    {(providersError as Error)?.message || "Unknown error"}
                  </Alert>
                )}
                {isConnectionsError && selectedProvider && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    data-testid="cloud-connection-error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => refetchConnections()}
                      >
                        Retry
                      </Button>
                    }
                  >
                    Failed to load connections:{" "}
                    {(connectionsError as Error)?.message || "Unknown error"}
                  </Alert>
                )}

                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 150 }}
                    data-testid="cloud-select-provider"
                  >
                    <InputLabel>Provider Type</InputLabel>
                    <Select
                      value={selectedProvider}
                      label="Provider Type"
                      onChange={(e) =>
                        handleProviderChange(
                          e.target.value as CloudProviderType,
                        )
                      }
                      disabled={isLoadingProviders || isProvidersError}
                      endAdornment={
                        isLoadingProviders ? (
                          <Box
                            sx={{
                              position: "absolute",
                              right: 24,
                              display: "flex",
                              alignItems: "center",
                              pl: 2,
                              zIndex: 1,
                              background:
                                "linear-gradient(to right, transparent, white 30%)",
                            }}
                          >
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                          </Box>
                        ) : null
                      }
                    >
                      {isLoadingProviders ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        providers?.map((provider) => (
                          <MenuItem key={provider.name} value={provider.name}>
                            {provider.display_name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{ flex: 1 }}
                    data-testid="cloud-select-connection"
                  >
                    <InputLabel>Provider Connection</InputLabel>
                    <Select
                      value={selectedConnectionId}
                      label="Provider Connection"
                      onChange={(e) =>
                        handleConnectionChange(e.target.value as number)
                      }
                      disabled={
                        !selectedProvider ||
                        isLoadingConnections ||
                        isConnectionsError
                      }
                      endAdornment={
                        isLoadingConnections ? (
                          <Box
                            sx={{
                              position: "absolute",
                              right: 24,
                              display: "flex",
                              alignItems: "center",
                              pl: 2,
                              zIndex: 1,
                              background:
                                "linear-gradient(to right, transparent, white 30%)",
                            }}
                          >
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                          </Box>
                        ) : null
                      }
                    >
                      {isLoadingConnections ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        connections?.map((conn) => (
                          <MenuItem key={conn.id} value={conn.id}>
                            {conn.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  Double-click folders to navigate. Click items to select them
                  for upload.
                </Typography>
              </Box>

              {/* Breadcrumb navigation - only show when connection is selected */}
              {selectedProvider && selectedConnectionId && (
                <CloudFileBreadcrumbs
                  currentPath={currentBreadcrumbPath}
                  onNavigate={handleBreadcrumbNavigate}
                />
              )}

              {/* File tree */}
              <Box
                sx={{ flex: 1, overflow: "auto", p: 2 }}
                data-testid="cloud-file-tree-container"
              >
                {!selectedProvider || !selectedConnectionId ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "text.secondary",
                    }}
                    data-testid="cloud-file-tree-empty"
                  >
                    <CloudUploadIcon
                      sx={{ fontSize: 64, mb: 2, opacity: 0.3 }}
                    />
                    <Typography>
                      Select a provider and connection to browse files
                    </Typography>
                  </Box>
                ) : (
                  <CloudFileTree
                    providerType={selectedProvider}
                    connectionId={selectedConnectionId}
                    selectionMode="multiple"
                    showDetails
                    height="100%"
                    selectedFiles={selectedFiles}
                    selectedFolders={selectedFolders}
                    onSelectionChange={handleSelectionChangeInternal}
                    onFolderNavigate={handleFolderNavigate}
                    focusedPath={currentBreadcrumbPath}
                    folderExtensionFilters={folderExtensionFilters}
                  />
                )}
              </Box>
            </Box>

            {/* Right Panel - Selected files */}
            <Box
              sx={{ flex: 1, minWidth: 300 }}
              data-testid="cloud-selected-files-panel"
            >
              <SelectedFilesPanel
                selectedFiles={selectedFiles}
                selectedFolders={selectedFolders}
                folderOptions={folderOptions}
                onRemoveFile={handleRemoveFile}
                onRemoveFolder={handleRemoveFolder}
                onUpdateFolderOptions={handleUpdateFolderOptions}
                onClearAll={clearSelection}
              />
            </Box>
          </Box>
        )}

        {/* Optional upload button when showUploadButton is true */}
        {showUploadButton &&
          !isLoadingPermissions &&
          canViewCloud &&
          canUseCloud && (
            <Box
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!canUpload || isUploading}
                startIcon={<CloudUploadIcon />}
                data-testid="cloud-content-upload-btn"
                sx={{
                  bgcolor: "#0891b2",
                  "&:hover": { bgcolor: "#0e7490" },
                }}
              >
                {isUploading
                  ? "Importing..."
                  : `Import ${
                      totalSelected > 0
                        ? `${totalSelected} Item${
                            totalSelected !== 1 ? "s" : ""
                          }`
                        : "Selected Files"
                    }`}
              </Button>
            </Box>
          )}
      </Box>
    );
  },
);

// Display name for dev tools
CloudFileSelectContent.displayName = "CloudFileSelectContent";

export default CloudFileSelectContent;
